import { useMemo } from 'react';

import { Radio, Tag } from '@arco-design/web-react';

import i18n from '@/i18n';

import { ARTIFACT_TYPE_OPTIONS } from '../constants';
import styles from '../index.module.less';
import type { CommonImageItemType } from '../types';
import { type ImageCardTag, buildImageCardTagsFromItem } from '../utils/imageCardDisplay';

const t = i18n.t.bind(i18n);

interface ImageCardProps {
  item: CommonImageItemType;
  /** 自定义 Tag；默认按制品类型与 packageFormat 动态生成 */
  tags?: ImageCardTag[];
  isSelected: boolean;
  disabled: boolean;
  /** 只读展示：不可点击，但不使用 disabled 灰色样式 */
  readOnly?: boolean;
  onChange: (key: string, item: CommonImageItemType) => void;
}

export function ImageCard({ item, tags, isSelected, disabled, readOnly = false, onChange }: ImageCardProps) {
  const nonInteractive = disabled || readOnly;

  const handleClick = () => {
    if (nonInteractive || isSelected) return;
    onChange?.(item.key, item);
  };
  const { artifactTypeLabel, artifactTypeColor, artifactTypeBackgroundColor } = useMemo(() => {
    const artifactTypeOption = ARTIFACT_TYPE_OPTIONS.find((o) => o.value === item.artifactType);
    return {
      artifactTypeLabel: artifactTypeOption?.label ?? t(String(item.artifactType).toLowerCase().replace('_', '.')),
      artifactTypeColor: artifactTypeOption?.color ?? undefined,
      artifactTypeBackgroundColor: artifactTypeOption?.backgroundColor ?? undefined,
    };
  }, [item.artifactType]);
  const cardTags = useMemo(() => tags ?? buildImageCardTagsFromItem(item), [item, tags]);

  return (
    <div
      onClick={handleClick}
      className={`${styles.imageCard} ${isSelected ? styles.selected : ''} ${disabled ? styles.imageCardDisabled : ''} ${readOnly ? styles.imageCardReadOnly : ''}`}
      role="button"
      tabIndex={nonInteractive ? -1 : 0}
      aria-disabled={disabled || undefined}
      aria-readonly={readOnly || undefined}
    >
      {artifactTypeLabel ? (
        <div className={styles.cardArtifactTypeTag} style={{ color: artifactTypeColor, backgroundColor: artifactTypeBackgroundColor }}>
          {artifactTypeLabel}
        </div>
      ) : null}
      <div className={styles.cardRadio}>
        <Radio disabled={disabled} checked={isSelected} />
      </div>
      <div className={`${styles.cardBody} ${artifactTypeLabel ? styles.cardBodyWithArtifactTag : ''}`}>
        <div className={styles.cardTitle}>
          {item.name}: {item.version}
          {item.description ? <span className={styles.cardDescription}>（{item.description}）</span> : null}
        </div>
        <div className={styles.cardTags}>
          {cardTags.map(({ label, value }, idx) => (
            <Tag key={idx} size="small" bordered>
              {label}：<span style={{ color: 'var(--color-text-3)' }}>{value}</span>
            </Tag>
          ))}
        </div>
      </div>
    </div>
  );
}
